import {axi, axiPayment, GetListOutput, GetListParams} from '@shared/api';
import {validateData} from '@shared/utils';
import {
  DepositDetails,
  PayoutDetails,
  ProviderRequestLog,
  Transaction,
} from '../types';
import {
  depositDetailsV8nSchema,
  payoutDetailsV8nSchema,
  v8nSchemaOfGetTransactionHistoryResponse,
  v8nSchemaOfProviderRequestLogsResponse,
} from './backendSchemas';
import {
  DepositDetailsOnBackend,
  GetProviderRequestLogsResponse,
  GetTransactionHistoryResponse,
  PayoutDetailsOnBackend,
} from './backendTypes';
import {
  mapDepositDetailsFromBackend,
  mapPayoutDetailsFromBackend,
  mapProviderRequestLogFromBackend,
  mapTransactionFromBackend,
} from './dataMappers';

// The values are backend-specific
export enum TransactionTypeFilter {
  all = 'all',
  credit = 'credit',
  debit = 'debit',
  coins = 'coins',
}

export interface GetTransactionHistoryParams extends GetListParams {
  typeFilter: TransactionTypeFilter;
}

export const getTransactionHistory = async ({
  typeFilter,
  page,
  pageSize,
}: GetTransactionHistoryParams): Promise<GetListOutput<Transaction>> => {
  const response = await axi.get<GetTransactionHistoryResponse>(
    '/balance/transactions/account',
    {
      params: {
        filter: typeFilter,
        page,
        per_page: pageSize,
      },
    },
  );

  validateData(
    response.data,
    v8nSchemaOfGetTransactionHistoryResponse,
    'getTransactionHistory',
  );

  const {
    array,
    total,
    total_pages,
    page: pageFromBackend,
    per_page,
  } = response.data;
  const items = array?.map(item => mapTransactionFromBackend(item)) ?? [];

  return {
    items,
    page: pageFromBackend,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

// The values are backend-specific (PaymentFilterEnum)
export enum PaymentStatusFilter {
  all = 'all',
  success = 'success',
  pending = 'pending',
  failed = 'failed',
}

const mapPaginatedTransactions = (
  data: GetTransactionHistoryResponse,
): GetListOutput<Transaction> => {
  const {array, total, total_pages, page, per_page} = data;

  return {
    items: array?.map(item => mapTransactionFromBackend(item)) ?? [],
    page,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

export interface GetUserTransactionsParams extends GetListParams {
  userId: string;
  filter?: TransactionTypeFilter;
}

export const getUserTransactions = async ({
  userId,
  page,
  pageSize,
  filter = TransactionTypeFilter.all,
}: GetUserTransactionsParams): Promise<GetListOutput<Transaction>> => {
  const response = await axiPayment.get<GetTransactionHistoryResponse>(
    `/admin/users/${userId}/transactions`,
    {params: {filter, page, per_page: pageSize}},
  );

  validateData(
    response.data,
    v8nSchemaOfGetTransactionHistoryResponse,
    'getUserTransactions',
  );

  return mapPaginatedTransactions(response.data);
};

export interface GetUserPoolTransactionsParams extends GetListParams {
  userId: string;
  relatedEntityId?: string;
}

export const getUserPoolTransactions = async ({
  userId,
  page,
  pageSize,
  relatedEntityId,
}: GetUserPoolTransactionsParams): Promise<GetListOutput<Transaction>> => {
  const response = await axiPayment.get<GetTransactionHistoryResponse>(
    `/admin/users/${userId}/transactions/pool`,
    {
      params: {
        page,
        per_page: pageSize,
        related_entity_id: relatedEntityId,
      },
    },
  );

  validateData(
    response.data,
    v8nSchemaOfGetTransactionHistoryResponse,
    'getUserPoolTransactions',
  );

  return mapPaginatedTransactions(response.data);
};

export interface GetUserPaymentsParams extends GetListParams {
  userId: string;
  filter?: PaymentStatusFilter;
}

export const getUserDeposits = async ({
  userId,
  page,
  pageSize,
  filter = PaymentStatusFilter.all,
}: GetUserPaymentsParams): Promise<GetListOutput<Transaction>> => {
  const response = await axiPayment.get<GetTransactionHistoryResponse>(
    `/admin/users/${userId}/deposits`,
    {params: {filter, page, per_page: pageSize}},
  );

  validateData(
    response.data,
    v8nSchemaOfGetTransactionHistoryResponse,
    'getUserDeposits',
  );

  return mapPaginatedTransactions(response.data);
};

export const getUserPayouts = async ({
  userId,
  page,
  pageSize,
  filter = PaymentStatusFilter.all,
}: GetUserPaymentsParams): Promise<GetListOutput<Transaction>> => {
  const response = await axiPayment.get<GetTransactionHistoryResponse>(
    `/admin/users/${userId}/payouts`,
    {params: {filter, page, per_page: pageSize}},
  );

  validateData(
    response.data,
    v8nSchemaOfGetTransactionHistoryResponse,
    'getUserPayouts',
  );

  return mapPaginatedTransactions(response.data);
};

export interface GetAllTransactionsParams extends GetListParams {
  filter?: TransactionTypeFilter;
  relatedEntityId?: string;
}

export const getAllTransactions = async ({
  page,
  pageSize,
  filter = TransactionTypeFilter.all,
  relatedEntityId,
}: GetAllTransactionsParams): Promise<GetListOutput<Transaction>> => {
  const response = await axiPayment.get<GetTransactionHistoryResponse>(
    '/admin/transactions',
    {
      params: {
        filter,
        page,
        per_page: pageSize,
        related_entity_id: relatedEntityId,
      },
    },
  );

  validateData(
    response.data,
    v8nSchemaOfGetTransactionHistoryResponse,
    'getAllTransactions',
  );

  return mapPaginatedTransactions(response.data);
};

export interface GetAllPoolTransactionsParams extends GetListParams {
  relatedEntityId?: string;
}

export const getAllPoolTransactions = async ({
  page,
  pageSize,
  relatedEntityId,
}: GetAllPoolTransactionsParams): Promise<GetListOutput<Transaction>> => {
  const response = await axiPayment.get<GetTransactionHistoryResponse>(
    '/admin/transactions/pool',
    {
      params: {
        page,
        per_page: pageSize,
        related_entity_id: relatedEntityId,
      },
    },
  );

  validateData(
    response.data,
    v8nSchemaOfGetTransactionHistoryResponse,
    'getAllPoolTransactions',
  );

  return mapPaginatedTransactions(response.data);
};

export interface GetAllPaymentsParams extends GetListParams {
  filter?: PaymentStatusFilter;
}

export const getAllDeposits = async ({
  page,
  pageSize,
  filter = PaymentStatusFilter.all,
}: GetAllPaymentsParams): Promise<GetListOutput<Transaction>> => {
  const response = await axiPayment.get<GetTransactionHistoryResponse>(
    '/admin/deposits',
    {params: {filter, page, per_page: pageSize}},
  );

  validateData(
    response.data,
    v8nSchemaOfGetTransactionHistoryResponse,
    'getAllDeposits',
  );

  return mapPaginatedTransactions(response.data);
};

export const getAllPayouts = async ({
  page,
  pageSize,
  filter = PaymentStatusFilter.all,
}: GetAllPaymentsParams): Promise<GetListOutput<Transaction>> => {
  const response = await axiPayment.get<GetTransactionHistoryResponse>(
    '/admin/payouts',
    {params: {filter, page, per_page: pageSize}},
  );

  validateData(
    response.data,
    v8nSchemaOfGetTransactionHistoryResponse,
    'getAllPayouts',
  );

  return mapPaginatedTransactions(response.data);
};

export interface GetPaymentDetailsParams {
  userId: string;
  paymentId: string;
}

export const getDepositDetails = async ({
  userId,
  paymentId,
}: GetPaymentDetailsParams): Promise<DepositDetails> => {
  const response = await axiPayment.get<DepositDetailsOnBackend>(
    `/admin/users/${userId}/deposits/${paymentId}`,
  );

  validateData(response.data, depositDetailsV8nSchema, 'getDepositDetails');

  return mapDepositDetailsFromBackend(response.data);
};

export const getPayoutDetails = async ({
  userId,
  paymentId,
}: GetPaymentDetailsParams): Promise<PayoutDetails> => {
  const response = await axiPayment.get<PayoutDetailsOnBackend>(
    `/admin/users/${userId}/payouts/${paymentId}`,
  );

  validateData(response.data, payoutDetailsV8nSchema, 'getPayoutDetails');

  return mapPayoutDetailsFromBackend(response.data);
};

const mapLogsResponse = ({
  array,
}: GetProviderRequestLogsResponse): ProviderRequestLog[] =>
  array.map(item => mapProviderRequestLogFromBackend(item));

export const getDepositLogs = async ({
  userId,
  paymentId,
}: GetPaymentDetailsParams): Promise<ProviderRequestLog[]> => {
  const response = await axiPayment.get<GetProviderRequestLogsResponse>(
    `/admin/users/${userId}/deposits/${paymentId}/logs`,
  );

  validateData(
    response.data,
    v8nSchemaOfProviderRequestLogsResponse,
    'getDepositLogs',
  );

  return mapLogsResponse(response.data);
};

export const getPayoutLogs = async ({
  userId,
  paymentId,
}: GetPaymentDetailsParams): Promise<ProviderRequestLog[]> => {
  const response = await axiPayment.get<GetProviderRequestLogsResponse>(
    `/admin/users/${userId}/payouts/${paymentId}/logs`,
  );

  validateData(
    response.data,
    v8nSchemaOfProviderRequestLogsResponse,
    'getPayoutLogs',
  );

  return mapLogsResponse(response.data);
};
